import classNames from 'classnames';

import Card from '../Card';
import type { CardProps } from '../Card';
import { CheckboxIcon, CheckboxFilledIcon, RadioIcon, RadioFilledIcon } from '../../icons/ui';

import styles from './_index.module.scss';

export interface SelectableCardProps extends CardProps {
  details?: React.ReactNode;
  error?: React.ReactNode;
  header?: React.ReactNode;
  helperText?: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  isMultiSelect?: boolean;
  onClick?: () => void;
  text?: React.ReactNode;
}

const SelectableCard = ({
  details,
  error,
  header,
  helperText,
  isActive,
  isDisabled,
  isMultiSelect,
  onClick,
  text,
  ...props
}: SelectableCardProps) => {
  const handleKeyDown = ({ code }: React.KeyboardEvent) => {
    if (isDisabled || !onClick) {
      return;
    }
    if (code === 'Enter' || code === 'Space') {
      onClick();
    }
  };

  const getCurrentIcon = () => {
    if (isActive) {
      return isMultiSelect ? <CheckboxFilledIcon /> : <RadioFilledIcon />;
    }
    return isMultiSelect ? <CheckboxIcon /> : <RadioIcon />;
  };

  return (
    <>
      <Card
        aria-disabled={isDisabled}
        aria-pressed={isActive}
        className={classNames(styles['selectable-card'], {
          [styles['is-active']]: isActive,
          [styles['is-clickable']]: !!onClick,
        })}
        onClick={isDisabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        {...props}
      >
        {helperText && <p className={styles['selectable-card-helper-text']}>{helperText}</p>}
        <div className={styles['selectable-card-content']}>
          <div className={styles['selectable-card-icon']}>{getCurrentIcon()}</div>
          <div>
            {header && <h5 className={styles['selectable-card-header']}>{header}</h5>}
            {typeof text === 'string' ? (
              <p
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
              />
            ) : (
              text
            )}

            {typeof details === 'string' ? (
              <p
                className={styles['selectable-card-details']}
                dangerouslySetInnerHTML={{
                  __html: details,
                }}
              />
            ) : (
              details
            )}
          </div>
        </div>
      </Card>
      {error && <div className={styles['selectable-card-error']}>{error}</div>}
    </>
  );
};

export default SelectableCard;
